import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/common/components/Alert/Alert';
import { Button } from '@/common/components/Button/Button';
import { Form } from '@/common/components/Form/Form';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import {
  CreatePasswordFormFields,
  useCreatePasswordForm,
} from '@/common/containers/Authentication/components/CreatePasswordForm/CreatePasswordForm';
import { ResetPasswordModal } from '@/common/containers/Authentication/components/ResetPasswordModal/ResetPasswordModal';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { useResetPassword } from '@/common/integrations/menhir/employee/sessions/sessions.hooks';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { useLocalSearchParams, useRouter } from 'expo-router';

export const ResetPasswordPage = () => {
  usePageViewTracking('reset_password_page');

  const { t } = useTranslation();
  const router = useRouter();
  const { token } = useLocalSearchParams<{ token: string }>();
  const { methods } = useCreatePasswordForm(token);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { mutateAsync: resetPassword } = useResetPassword();

  const onSubmit = async (form: {
    password: string;
    passwordConfirmation: string;
    token: string;
  }) => {
    setError(false);
    AnalyticsService.trackCustomEvent('reset_password_button_clicked');
    try {
      await resetPassword({ token, password: form.password });
      router.push(ROUTES.SIGNIN);
    } catch {
      setError(true);
    }
  };

  return (
    <AuthFormLayout
      footer={
        <Button
          onPress={methods.handleSubmit(onSubmit)}
          loading={methods.formState.isSubmitting}
        >
          {t('authentication.reset_password.confirm.button')}
        </Button>
      }
    >
      <Stack gap="xl">
        <PageHeader title={t('authentication.reset_password.confirm.title')} />
        {error && (
          <Alert type="error">
            <Translate
              i18nKey="authentication.reset_password.link_error"
              components={{
                action: <TextAction onPress={() => setShowModal(true)} />,
              }}
            />
          </Alert>
        )}
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <CreatePasswordFormFields />
          </Form>
        </FormProvider>
      </Stack>
      {showModal && <ResetPasswordModal onClose={() => setShowModal(false)} />}
    </AuthFormLayout>
  );
};
